// fomService will be refactored and probably moved so we dont need to fix eslint error

/* eslint-disable no-empty */
import { getLocalData, WebPlayActionCardService, getHighchartAsPng } from '@playbooks/web-play-component'
import { FileQuestionNode, RecommendationNode, ActionCardModel, IInputNode } from '@playbooks/action-cards-utils'

import { FomToken } from './FomToken'
import DOMPurify from 'dompurify'

const FlowStatuses: Record<string, number> = {
  play: 1540,
  pause: 1541,
  stop: 1542,
  complete: 1545,
}

const FOM_SERVICE_URL = import.meta.env.VITE_FOM_SERVICE_URL

const fomService = {
  async getWebPlayInfo(playbookId: string | null) {
    const response = await fetch(`${FOM_SERVICE_URL}/getWebPlayInfo?playbookId=${playbookId}`)
    if (!response.ok) {
      return { statusCode: response.status, webPlayInfo: null }
    }

    const info = await response.json()
    // info can have status code if a user doesn't have access
    // If we have webPlayInfo in the response we set status code to 200 we self
    // because we have logic depended on status code
    const statusCode = info.statusCode || 200
    return {
      statusCode,
      webPlayInfo: info,
    }
  },

  async createPlay(
    playbookId: string,
    title: string,
    keywords: string[],
    score: number,
    scoringDimensions: unknown[],
    lastActiveSeparator: string | undefined,
    flow: string,
    loggedInUserId: string,
  ) {
    const url = new URL(`${FOM_SERVICE_URL}/createPlay`)
    url.searchParams.append('playbookId', playbookId)
    url.searchParams.append('title', title)
    url.searchParams.append('keywords', keywords.join(','))
    url.searchParams.append('score', String(score))
    url.searchParams.append('lastActiveSeparator', lastActiveSeparator || '')
    url.searchParams.append('flow', String(FlowStatuses[flow] || FlowStatuses.play))
    url.searchParams.append('loggedInUserId', loggedInUserId)

    const formData = new FormData()
    try {
      const scoringDimensionsString = scoringDimensions?.length > 0 ? JSON.stringify(scoringDimensions) : null
      if (scoringDimensionsString) {
        formData.append('scoringDimensions', scoringDimensionsString)
      }
    } catch {}

    const playCreationResponse = await fetch(url, {
      method: 'POST',
      body: formData,
    })
    if (playCreationResponse.status !== 200) {
      throw new Error('Failed to create play: ' + playCreationResponse.statusText)
    }
    const fomToken = new FomToken(playCreationResponse)
    const playId = await playCreationResponse.text()

    return { playId, fomToken }
  },

  async attachFileToPlay(
    playbookId: string,
    playId: string,
    headerId: string,
    headerName: string,
    file: File,
    fomToken: unknown,
  ): Promise<[number, string]> {
    if (file.size > 30 * 1024 * 1024) {
      throw new Error('File size exceeds the limit of 30MB')
    }

    const url = new URL(`${FOM_SERVICE_URL}/attachFileToPlay`)
    url.searchParams.append('playbookId', playbookId)
    url.searchParams.append('playId', playId)
    url.searchParams.append('headerId', headerId)
    url.searchParams.append('headerName', headerName)

    const formData = new FormData()
    formData.append('file', file)

    const headers = (fomToken as FomToken).appendToObject({})

    const response = await fetch(url, {
      method: 'POST',
      body: formData,
      headers: headers,
    })

    if (!response.ok) {
      console.error('Failed to attach file to project file:', response.statusText)
      throw new Error('Server error status ' + response.status)
    }

    const fileData = await response.json()
    return [fileData.id, fileData.url]
  },

  async attachActionCardToPlay(playbookId: string, playId: string, actionCard: unknown, fomToken: unknown) {
    const url = new URL(`${FOM_SERVICE_URL}/attachActionCardToPlay`)
    url.searchParams.append('playbookId', playbookId.toString())
    url.searchParams.append('playId', playId.toString())

    const headers = (fomToken as FomToken).appendToObject({})
    headers['Content-Type'] = 'application/json'

    const response = await fetch(url, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(actionCard),
    })

    if (!response.ok) {
      console.error('Failed to attach action card to play:', response.statusText)
      throw new Error('Server error status ' + response.status)
    }
  },

  async sendPlayCreatedNotification(
    playbookId: string,
    playId: string,
    email: string,
    keywords: string[],
    actionCardModel: ActionCardModel,
    fomToken: unknown,
  ) {
    const url = new URL(`${FOM_SERVICE_URL}/sendPlayCreatedNotification`)
    url.searchParams.append('playbookId', playbookId.toString())
    url.searchParams.append('playId', playId.toString())

    const headers = (fomToken as FomToken).appendToObject({})

    const formData = new FormData()
    formData.append('email', email)
    formData.append('keywords', keywords.join(','))
    formData.append('actionCardData', getActionCardDataReplacementInfo(actionCardModel))

    const pngBlob = await getHighchartAsPng(actionCardModel.data)
    if (pngBlob) {
      formData.append('scoringDimensionsChart', pngBlob as Blob)
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: headers,
      body: formData,
    })

    if (!response.ok) {
      console.error('Failed to attach action card to play:', response.statusText)
      throw new Error('Server error status ' + response.status)
    }
  },

  async createAnonymousToken() {
    const response = await fetch(`${FOM_SERVICE_URL}/createAnonymousToken`)
    return await response.text()
  },

  async getActionCard({
    playbookId,
    actionCardUrl,
    headersCount,
    email,
  }: {
    playbookId: string | null
    actionCardUrl: string
    headersCount: number
    email: string | null
  }) {
    const localData = await getLocalData(playbookId!)

    if (localData) {
      return localData
    }
    if (actionCardUrl) {
      const response = await fetch(actionCardUrl)
      const data = await response.json()

      const actionCardService = new WebPlayActionCardService(data)

      actionCardService.adjustEmail(email)
      actionCardService.adjustModelFromQueryString()

      if (headersCount === 1 && data.sections.length && data.sections[0].type === 'Separator') {
        // expand single separator if it comes first as it will be hidden
        data.sections[0].isOpened = true
      }

      return data
    } else {
      throw new Error('No action card found for playbookId: ' + playbookId)
    }
  },

  async getPlaybookId(code: string) {
    const infoResponse = await fetch(`${FOM_SERVICE_URL}/getWebPlayIdByCode?code=${code}`)
    return await infoResponse.json()
  },

  async rollbackPlayCreation(playbookId: string, playId: string, fomToken: unknown) {
    try {
      const url = new URL(`${FOM_SERVICE_URL}/RollbackPlayCreation`)
      url.searchParams.append('playbookId', playbookId)
      url.searchParams.append('playId', playId)

      const headers = (fomToken as FomToken).appendToObject({})

      const response = await fetch(url, {
        method: 'POST',
        headers: headers,
      })

      if (!response.ok) {
        console.error('Failed to rollback play creation:', response.statusText)
      }
    } catch (ex) {
      console.error('Failed to rollback play creation', ex)
    }
  },
}

function getActionCardDataReplacementInfo(actionCardModel: ActionCardModel): string {
  const rows: string[] = []
  const recommendationRows: string[] = []

  for (const node of actionCardModel.getSearchService().filterVisible()) {
    if (node instanceof FileQuestionNode) {
      const fileQuestionNode = node as FileQuestionNode
      const fileQuestionHtmlValues: string[] = []
      for (const fileItem of fileQuestionNode.getValue() ?? []) {
        if (fileItem.id > 0 && !fileItem.error) {
          const fileHtmlValue = `<a href="${escapeHtml(fileItem.url || '')}" target="_blank" rel="noopener noreferrer">${escapeHtml(fileItem.name)}</a>`
          fileQuestionHtmlValues.push(fileHtmlValue)
        }
      }
      rows.push(
        `<tr><td>${escapeHtml(fileQuestionNode.getLabel())}: </td><td>${fileQuestionHtmlValues.join('; ')}</td></tr>`,
      )
      continue
    }
    if (node instanceof RecommendationNode) {
      const recommendationNode = node as RecommendationNode
      if (recommendationNode.includeInWebPlayNotification) {
        recommendationRows.push(`<tr><td colspan="2">${escapeHtml(recommendationNode.name)}:</td></tr>`)
        recommendationRows.push(`<tr><td colspan="2">${prepareRecommendationNodeText(recommendationNode)}</td></tr>`)
      }
      continue
    }
    const inputNode = node as unknown as IInputNode<unknown>
    if (inputNode.IInputNode && !inputNode.isInputEmpty()) {
      const value = escapeHtml(inputNode.stringifyValue()).replaceAll('\n', '<br/>')
      rows.push(`<tr><td>${escapeHtml(node.getLabel())}: </td><td>${value}</td></tr>`)
    }
  }

  if (recommendationRows.length > 0) {
    if (rows.length > 0) {
      // add separator before recommendations if we have other nodes to show
      rows.push('<tr><td colspan="2"></td></tr>')
    }
    rows.push(...recommendationRows)
  }

  if (!rows.length) {
    return ''
  }

  return `<table style="border-spacing: 2px 4px; width: 100%;"><tbody style="vertical-align: top;">${rows.join(
    '\n',
  )}</tbody></table>`
}

function escapeHtml(text: string): string {
  return text
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;')
}

function prepareRecommendationNodeText(node: RecommendationNode): string {
  const sanitizedText = DOMPurify.sanitize(node.text)
  const parser = new DOMParser()
  const doc = parser.parseFromString(sanitizedText, 'text/html')

  applyStylesToElements(doc.querySelectorAll('img'), 'max-width: 100%;')
  // The color must be oklch(92.2% 0 0), but Gmail does not support the 'oklch' format, therefore use a hex fallback (e.g. `#e2e2e2`) in inline styles
  // If a style attribute contains unknown commands, the entire attribute value will be ignored
  applyStylesToElements(doc.querySelectorAll('table tbody tr td'), 'border: 1px solid #e2e2e2')

  for (const [className, style] of Object.entries(cssClassesMap)) {
    applyStylesToElements(doc.querySelectorAll(`.${escapeSelectorToken(className)}`), style)
  }

  return doc.body.innerHTML
}

function applyStylesToElements(elements: NodeListOf<Element>, style: string): void {
  elements.forEach(element => {
    const existingStyle = element.getAttribute('style') ?? ''
    element.setAttribute('style', mergeStyles(existingStyle, style))
  })
}

function mergeStyles(existingStyle: string, styleToAdd: string): string {
  const normalizedExistingStyle = existingStyle.trim()
  const normalizedStyleToAdd = styleToAdd.trim()

  if (!normalizedExistingStyle) {
    return normalizedStyleToAdd
  }

  if (!normalizedStyleToAdd) {
    return normalizedExistingStyle
  }

  return `${normalizedExistingStyle}; ${normalizedStyleToAdd}`
}

// sometimes styles may have forbidden characters (":", "[]" and etc) for CSS selectors, so we need to escape them
function escapeSelectorToken(token: string): string {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(token)
  }

  return token.replace(/([!"#$%&'()*+,./:;<=>?@[\\\]^`{|}~])/g, '\\$1')
}

// CSS-classes "py-"", "px-"", "my-" originally map to "padding-block", "padding-inline", "margin-blok" (respectively) but Gmail does not support these logical properties
// If a style attribute contains unknown commands, the entire attribute value will be ignored
const cssClassesMap: Record<string, string> = {
  'pl-3': 'padding-left: 12px',
  'p-4': 'padding: 16px',
  'py-2': 'padding-top: 8px; padding-bottom: 8px',
  'py-5': 'padding-top: 20px; padding-bottom: 20px',
  'px-4': 'padding-left: 16px; padding-right: 16px',
  'my-1': 'margin-top: 4px; margin-bottom: 4px',
  'rounded-sm': 'border-radius: 6px',
}

export default fomService
